import { observable, action, computed } from "mobx";
import { LoginService } from "modules/login/LoginService";
import {
  createBlankUser,
  createUserModel,
  User,
} from "modules/login/models/User";
import { default as firebase, firestore } from "common/Firebase";
import { ProfileService } from "modules/profile/ProfileService";
/**
 * Store de autenticação.
 * @category Users
 * @hideconstructor
 */
class LoginStore {
  @observable userAuth: User | undefined;
  @observable firstLogin: boolean = false;
  @observable isUserAuth: boolean = false;
  @observable recentLogin: boolean = false;
  newSessionError: boolean = false;
  invalidAccessError: boolean = false;
  developmentsSnapshot: any = undefined;
  modelMapsSnapshot: any = undefined;
  rateLibrariesSnapshot: any = undefined;
  wbsFileImportSnapshot: any = undefined;
  wbsFileExportSnapshot: any = undefined;
  wbsImportFileSnapshot: any = undefined;
  copyDevelopmentSnapshot: any = undefined;
  filesSnapshot: any = undefined;
  usersSnapshot: any = undefined;
  authListener: any = undefined;
  rateLibrariesUsersSnapshot: any = undefined;
  rateLibrariesPublicSnapshot: any = undefined;
  takeOffFormatedItemsSnapshot: any = undefined;
  wbsRowsSnapshot: any = undefined;

  @observable shareUsersIDs: any = [];
  @observable shareUsersObj: any = [];

  resetStore() {
    this.userAuth = undefined;
    this.firstLogin = false;
    this.recentLogin = false;
  }

  @computed get getUserAuth(): User {
    return this.userAuth != undefined ? this.userAuth : createBlankUser();
  }

  @action
  async setShareUsersObj(user) {
    this.shareUsersObj.push(user);
  }

  @action
  async setShareUsersIDs(id) {
    this.shareUsersIDs.push(id);
  }

  async getSharedUser(id) {
    return this.shareUsersObj.find((user) => id == user.uid);
  }

  @action
  async checkUserAlreadyExistsInArray(id) {
    let check = this.shareUsersIDs.includes(id);
    if (check) {
      return true;
    }
    this.setShareUsersIDs(id);
    return false;
  }

  addListenerToVerifySession = (): Promise<User | null> => {
    let firstTime = true;
    return new Promise(async (resolve, reject) => {
      try {
        this.clearAuthSnapshot();
        this.authListener = firebase.auth().onAuthStateChanged(async (user) => {
          if (firstTime) {
            await this.loadCurrentUser();
            firstTime = false;
          }

          const isAuth = this.isUserAuth;
          const userAuth = this.userAuth;

          if (!isAuth || !userAuth) {
            return resolve(null);
          }

          if (!user || !user.email) {
            throw Error("User not defined.");
          }

          this.clearUsersSnapshot();
          this.usersSnapshot = await firestore
            .collection("users")
            .where(`uid`, "==", userAuth.uid)
            .onSnapshot(async (snapshot) => {
              const data = snapshot.docs[0].data();

              userAuth.changeDescription = data.changeDescription;

              const lastToken =
                data && data.lastSignOnOrcamento
                  ? data.lastSignOnOrcamento.token
                  : "";

              if (userAuth.token != lastToken) {
                this.newSessionError = true;
                await this.signOutUser();
                return;
              }
            });
          return resolve(userAuth);
        });
      } catch (e) {
        reject(e);
      }
    });
  };

  @action
  async loadCurrentUser(): Promise<void> {
    const currentUser = ProfileService.getAuthData();
    if (!currentUser) return;

    const user = await this.getAuthenticatedUserData();
    if (user) {
      if (!user.products || !user.products.orcamento) {
        this.invalidAccessError = true;
        await this.signOutUser();
        return;
      }

      this.userAuth = user;
      this.isUserAuth = Boolean(user);

      this.refreshUserToken(
        user,
        currentUser.refreshToken,
        currentUser.metadata.lastSignInTime
      );
    }
  }

  async getAuthenticatedUserData(): Promise<User | undefined> {
    const currentUser = ProfileService.getAuthData();
    if (currentUser) {
      const user = await LoginService.fetchUserByID(currentUser.uid);
      return user;
    }
    return undefined;
  }

  async getUser(uid: string): Promise<User | undefined> {
    if (!uid) return undefined;
    return LoginService.fetchUserByID(uid);
  }

  private async refreshUserToken(
    user: User,
    token: string,
    lastSignInTime: string = ""
  ) {
    if (!user) return;

    const lastSignOnOrcamento = { token, lastSignInTime };
    await firestore
      .collection("users")
      .doc(user.uid)
      .update({ token, lastSignInTime, lastSignOnOrcamento });

    user.token = token;
    user.lastSignInTime = lastSignInTime;
    user.lastSignOnOrcamento = lastSignOnOrcamento;
  }

  @action
  async addUser(): Promise<void> {
    const currentUser = ProfileService.getAuthData();
    let user = createBlankUser();
    if (currentUser) {
      user = createUserModel({
        uid: currentUser.uid,
        displayName: currentUser.displayName || "",
        email: currentUser.email || "",
        emailVerified: currentUser.emailVerified || false,
        isAnonymous: currentUser.isAnonymous || false,
        phoneNumber: currentUser.phoneNumber || "",
        photoURL: currentUser.photoURL || "",
        refreshToken: currentUser.refreshToken,
        providerId: currentUser.providerId || "",
      });
    }
    await LoginService.addUser(user);
  }

  @action
  async signOutUser() {
    this.clearAllSnapshots();
    await LoginService.signOutUser();
    this.isUserAuth = false;
    this.userAuth = undefined;
  }

  /** Retorna se o usuário atual é trial ou não. */
  isTrialUser(): boolean {
    if (this.userAuth) {
      return this.userAuth.trial;
    }
    return false;
  }

  /** Retorna a quantidade de dias restantes para terminar o período de trial do usuário. */
  timeLeft(): number {
    if (this.userAuth) {
      let date: any = new Date(this.userAuth.creationTime);
      let today: any = new Date();
      let calcDate = today - date;
      const diffInDays = calcDate / (1000 * 60 * 60 * 24);
      return 30 - Math.floor(diffInDays);
    }
    return 0;
  }

  /** Define que o usuário já fez o primeiro login. */
  @action
  async ChangeFirstLogin() {
    if (this.userAuth) {
      await LoginService.changeFirstLogin(this.userAuth);
      this.userAuth.firstLogin = false;
    }
  }

  /** Altera a propriedade que define se será exibido o tutorial para o usuário ou não. */
  @action
  async changeShowTutorial(value: boolean) {
    if (this.userAuth) {
      await LoginService.changeShowTutorial(this.userAuth, value);
      this.recentLogin = false;
      this.userAuth.showTutorial = value;
    }
  }

  /** Retorna se o período de trial do usuário já terminou. */
  expiredTrial(): boolean {
    if (this.userAuth && this.userAuth.trialExpired) return true;
    if (this.isTrialUser()) return this.timeLeft() < 0;
    return false;
  }

  /** Retorna se usuário trial tem o email verificado.
   * Caso o usuário não seja trial é assumido que seu email é verificado. */
  trialUserIsVerified(): boolean {
    const user = this.userAuth;
    return user && user.trial ? Boolean(user.emailVerified) : true;
  }

  clearAllSnapshots() {
    this.clearAuthSnapshot();
    this.clearUsersSnapshot();
    this.clearDevelopmentsSnapshot();
    this.clearModelMapsSnapshot();
    this.clearRateLibrariesSnapshot();
    this.clearWbsFileImportSnapshot();
    this.clearWbsFileExportSnapshot();
    this.clearWbsImportFileSnapshot();
    this.clearFilesSnapshot();
    this.clearRateLibrariesUsersSnapshot();
    this.clearRateLibrariesPublicSnapshot();
    this.clearTakeOffFormatedItemsSnapshot();
    this.clearWbsRowsSnapshot();
  }

  clearAuthSnapshot() {
    if (this.authListener) this.authListener();
  }

  clearDevelopmentsSnapshot() {
    if (this.developmentsSnapshot) this.developmentsSnapshot();
  }

  clearWbsRowsSnapshot() {
    if (this.wbsRowsSnapshot) this.wbsRowsSnapshot();
  }

  clearRateLibrariesSnapshot() {
    if (this.rateLibrariesSnapshot) this.rateLibrariesSnapshot();
  }

  clearWbsFileImportSnapshot() {
    if (this.wbsFileImportSnapshot) this.wbsFileImportSnapshot();
  }

  clearWbsFileExportSnapshot() {
    if (this.wbsFileExportSnapshot) this.wbsFileExportSnapshot();
  }

  clearWbsImportFileSnapshot() {
    if (this.wbsImportFileSnapshot) this.wbsImportFileSnapshot();
  }

  clearFilesSnapshot() {
    if (this.filesSnapshot) this.filesSnapshot();
  }

  clearUsersSnapshot() {
    if (this.usersSnapshot) this.usersSnapshot();
  }

  clearModelMapsSnapshot() {
    if (this.modelMapsSnapshot) this.modelMapsSnapshot();
  }

  clearRateLibrariesUsersSnapshot() {
    if (this.rateLibrariesUsersSnapshot) this.rateLibrariesUsersSnapshot();
  }

  clearRateLibrariesPublicSnapshot() {
    if (this.rateLibrariesPublicSnapshot) this.rateLibrariesPublicSnapshot();
  }

  clearTakeOffFormatedItemsSnapshot() {
    if (this.takeOffFormatedItemsSnapshot) this.takeOffFormatedItemsSnapshot();
  }
}

const store = new LoginStore();
export { store as LoginStore };



// WEBPACK FOOTER //
// ./src/modules/login/LoginStore.ts